import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api/auth';
import deltaLogo from '../../assets/logo.png';
import styles from './Login.module.css';

const VIEW = { LOGIN: 'login', SIGNUP: 'signup', CONFIRM: 'confirm' };

/* ── Step indicator ── */
function Steps({ current }) {
  const map    = { login: 0, signup: 1, confirm: 2 };
  const active = map[current] ?? 0;
  return (
    <div className={styles.steps}>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className={`${styles.step}
            ${i < active  ? styles.stepDone   : ''}
            ${i === active ? styles.stepActive : ''}`}
        />
      ))}
    </div>
  );
}

/* ── OTP 6-digit box ── */
function OtpBox({ value, onChange }) {
  const refs   = useRef([]);
  const digits = value.padEnd(6, ' ').slice(0, 6).split('');

  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = digits.map((d, i) => (i === idx ? ' ' : d)).join('');
      onChange(next.trimEnd());
      if (idx > 0) refs.current[idx - 1]?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    e.preventDefault();
    const next = digits.map((d, i) => (i === idx ? e.key : d)).join('').trimEnd();
    onChange(next.slice(0, 6));
    if (idx < 5) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted   = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted);
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className={styles.otpWrap}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={el => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i]?.trim() ?? ''}
          className={`${styles.otpInput} ${digits[i]?.trim() ? styles.filled : ''}`}
          onChange={() => {}}
          onKeyDown={e => handleKey(e, i)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}

/* ── Logo block — shared ── */
function LogoBlock() {
  return (
    <div className={styles.logoWrap}>
      <img src={deltaLogo} alt="DeltaDesk" className={styles.logoImg} />
      <div className={styles.appName}>Delta<span>Desk</span></div>
      <div className={styles.tagline}>Algorithmic Trading Platform</div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   LOGIN VIEW
══════════════════════════════════════════════ */
function LoginView({ onSwitch }) {
  const { login: authLogin } = useAuth();
  const navigate             = useNavigate();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true);
    setError('');
    try {
      await authLogin(email.trim(), password);
      navigate('/dashboard');
    } catch (err) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LogoBlock />
      <Steps current={VIEW.LOGIN} />
      <div className={styles.viewTitle}>Welcome back</div>
      <div className={styles.viewSub}>Sign in to your trading account</div>

      {error && <div className={styles.errorBanner}><span>⚠</span> {error}</div>}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Email</label>
          <input
            type="email"
            className={styles.input}
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            disabled={loading}
          />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Password</label>
          <input
            type="password"
            className={styles.input}
            placeholder="••••••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
          />
        </div>
        <button
          type="submit"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={loading}
        >
          <span className={styles.btnInner}>
            {loading && <span className={styles.spinner} />}
            {loading ? 'Signing in…' : 'Sign in'}
          </span>
        </button>
      </form>

      <div className={styles.footerRow}>
        No account?
        <button className={styles.link} onClick={() => onSwitch(VIEW.SIGNUP)}>
          Create one
        </button>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════
   SIGNUP VIEW
══════════════════════════════════════════════ */
function SignupView({ onSwitch, onConfirm }) {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !confirm) { setError('Please fill in all fields.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      /* authAPI.signup accepts a data object */
      await authAPI.signup({ email: email.trim(), password });
      onConfirm(email.trim());
    } catch (err) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Signup failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LogoBlock />
      <Steps current={VIEW.SIGNUP} />
      <div className={styles.viewTitle}>Create account</div>
      <div className={styles.viewSub}>Start trading algorithmically</div>

      {error && <div className={styles.errorBanner}><span>⚠</span> {error}</div>}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Email</label>
          <input
            type="email"
            className={styles.input}
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            disabled={loading}
          />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Password</label>
          <input
            type="password"
            className={styles.input}
            placeholder="Min 8 characters"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={loading}
          />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Confirm Password</label>
          <input
            type="password"
            className={styles.input}
            placeholder="••••••••••••"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoComplete="new-password"
            disabled={loading}
          />
        </div>
        <button
          type="submit"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={loading}
        >
          <span className={styles.btnInner}>
            {loading && <span className={styles.spinner} />}
            {loading ? 'Creating account…' : 'Create account'}
          </span>
        </button>
      </form>

      <div className={styles.footerRow}>
        Already have an account?
        <button className={styles.link} onClick={() => onSwitch(VIEW.LOGIN)}>
          Sign in
        </button>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════
   CONFIRM OTP VIEW
══════════════════════════════════════════════ */
function ConfirmView({ email, onSwitch }) {
  const [otp,      setOtp]      = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [resendCd, setResendCd] = useState(0);

  /* resend countdown */
  useEffect(() => {
    if (resendCd <= 0) return;
    const t = setTimeout(() => setResendCd(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCd]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (otp.replace(/\s/g, '').length < 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      /* authAPI.confirm(email, code) */
      await authAPI.confirm(email, otp.replace(/\s/g, ''));
      setSuccess('Email confirmed! Redirecting to sign in…');
      setTimeout(() => onSwitch(VIEW.LOGIN), 1800);
    } catch (err) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCd > 0) return;
    setError('');
    try {
      /* authAPI.resendCode(email) */
      await authAPI.resendCode(email);
      setResendCd(60);
      setSuccess('New code sent — check your inbox.');
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to resend code.');
    }
  };

  return (
    <>
      <LogoBlock />
      <Steps current={VIEW.CONFIRM} />
      <div className={styles.viewTitle}>Verify your email</div>
      <div className={styles.viewSub}>
        We sent a 6-digit code to <strong>{email}</strong>
      </div>

      {error   && <div className={styles.errorBanner}><span>⚠</span> {error}</div>}
      {success && <div className={styles.successBanner}><span>✓</span> {success}</div>}

      <form className={styles.form} onSubmit={handleConfirm}>
        <OtpBox value={otp} onChange={setOtp} />

        <button
          type="submit"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={loading || otp.replace(/\s/g, '').length < 6}
        >
          <span className={styles.btnInner}>
            {loading && <span className={styles.spinner} />}
            {loading ? 'Verifying…' : 'Verify & continue'}
          </span>
        </button>
      </form>

      <div className={styles.resendRow}>
        Didn't receive it?
        <button
          className={styles.resendBtn}
          onClick={handleResend}
          disabled={resendCd > 0}
        >
          {resendCd > 0 ? `Resend in ${resendCd}s` : 'Resend code'}
        </button>
      </div>

      <div className={styles.footerRow}>
        <button className={styles.link} onClick={() => onSwitch(VIEW.LOGIN)}>
          ← Back to sign in
        </button>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════
   ROOT — manages active view + email handoff
══════════════════════════════════════════════ */
export default function Login() {
  const [view,  setView]  = useState(VIEW.LOGIN);
  const [email, setEmail] = useState('');

  const handleConfirm = (confirmedEmail) => {
    setEmail(confirmedEmail);
    setView(VIEW.CONFIRM);
  };

  return (
    <div className={styles.page}>
      <div className={styles.glowLeft} />
      <div className={styles.card}>
        {view === VIEW.LOGIN   && <LoginView   onSwitch={setView} />}
        {view === VIEW.SIGNUP  && <SignupView  onSwitch={setView} onConfirm={handleConfirm} />}
        {view === VIEW.CONFIRM && <ConfirmView email={email} onSwitch={setView} />}
      </div>
    </div>
  );
}